import FeatureLayer from '@arcgis/core/layers/FeatureLayer';
import Layer from '@arcgis/core/layers/Layer';
import MapImageLayer from '@arcgis/core/layers/MapImageLayer';
import { isCompanionSecondaryRasterId } from '../companionLayerTitles';
import {
  GISService,
  GISStateSublayer,
  GISStateLayer,
  GISServiceTypes,
} from '../gisServicesTypes';

// Trying to massage esri types into a union of layer properties is a mess, and
// doing type guards becomes extremely verbose. This union has the properties that
// might exist on a layer or sublayer:
type TreeLayer = Layer &
  Partial<Omit<MapImageLayer, 'type'>> &
  Partial<Omit<FeatureLayer, 'type'>> &
  Partial<__esri.Sublayer> & {
    layers?: __esri.Collection<TreeLayer>;
    sublayers?: __esri.Collection<TreeLayer>;
  };

// We can't put real map GIS layer objects into RTK redux state (the circular
// references explode immer) so we store them in LAYER_MAP and push them back
// into the tree in the selectors.
const LAYER_MAP: Record<string, TreeLayer> = {};
const SUBLAYER_MAP: Record<string, Layer & Partial<__esri.Sublayer>> = {};

/**
 * Clears all entries from LAYER_MAP and SUBLAYER_MAP.
 * Call this when switching map providers to ensure stale ESRI layer objects
 * are not reused after the map is recreated.
 */
export function clearLayerMaps(): void {
  for (const key in LAYER_MAP) {
    delete LAYER_MAP[key];
  }
  for (const key in SUBLAYER_MAP) {
    delete SUBLAYER_MAP[key];
  }
}

export function gisLayerFor(entry: GISStateLayer | { id: string }) {
  return LAYER_MAP[entry.id];
}

export function gisSublayerFor(entry: GISStateSublayer) {
  return SUBLAYER_MAP[entry.uniqueId];
}

/** Takes a POJO map of layers and sublayers, and inserts GIS layers and
 * sublayers. */
export function reifyLayerMap(layers: GISStateLayer[]) {
  return layers.map(layer => {
    return {
      ...layer,
      gisLayer: gisLayerFor(layer),
      sublayers: layer.sublayers.map(sublayer => {
        return {
          ...sublayer,
          gisSublayer: gisSublayerFor(sublayer),
        };
      }),
    };
  });
}

/**
 * GIS service layers have sublayers and sub-sub layers and sub-sub-sub... Our
 * UI is interested in the topmost and bottommost of these -- the root that
 * groups the layers together, and leaves that contain actual map data.
 *
 * This function takes a layer and fills in its sublayers.
 */
export function listSublayers(
  rootLayer: Layer,
  service: GISService,
): GISStateLayer {
  const resultList: GISStateSublayer[] = [];
  const parent = rootLayer as TreeLayer;
  LAYER_MAP[service.gisLayerID] = parent;
  const titles: Record<string, number> = {};

  // Process sublayers if either the layer supports sublayer visibility (MapImageLayer)
  // or if the layer has sublayers (WMSLayer, FeatureLayer, etc.)
  const hasSubLayers = getSublayers(parent).length > 0;
  const supportsSubLayerVisibility =
    !!parent.capabilities?.exportMap?.supportsSublayerVisibility;

  if (supportsSubLayerVisibility || hasSubLayers) {
    try {
      // MapImageLayers have allSublayers property, which flattens out the tree
      // for us; but I can't find documentation of the same property for Feature
      // layers, so I've stuck with breadth-first descent
      const layersList = getSublayers(parent);
      while (layersList.length) {
        const currentLayer = layersList.pop();
        // Redundant, but typescript doesn't recognize the while as a type
        // guard:
        if (!currentLayer) continue;

        // Secondary-CQL companion rasters (e.g. MACRO) are toggled beside the
        // Level picker — keep them off the Map Layers checkbox list.
        const currentId = String(
          (currentLayer as { id?: string | number }).id ?? '',
        );
        if (isCompanionSecondaryRasterId(currentId)) {
          continue;
        }

        const sublayers = getSublayers(currentLayer);

        if (!sublayers.length) {
          // Get sublayer identifier - different layer types use different properties:
          // - MapImageLayer sublayers: layerId or id (number)
          // - WMS sublayers: name (string)
          // - WMTS sublayers: id (string)
          const sublayerId =
            currentLayer.layerId ??
            currentLayer.id ??
            (currentLayer as { name?: string }).name ??
            resultList.length;
          const uniqueId = `${service.gisLayerID}-${sublayerId}`;
          SUBLAYER_MAP[uniqueId] = currentLayer;

          // Prefer REST layer name over Esri's often-generic GroupLayer title
          // (FeatureServer children can all share the parent service title).
          let title =
            currentLayer.sourceJSON?.name ||
            currentLayer.title ||
            (currentLayer as { name?: string }).name ||
            service.display_name;
          // Sublayers can have the same title in different parents;
          // do something to make them unique.
          if (!titles[title]) {
            titles[title] = 1;
          } else {
            title = `${title} ${titles[title]}`;
            titles[title]++;
          }

          // Determine if sublayer should be active initially
          const isActive =
            !!service.auto_checked ||
            service.service_type === GISServiceTypes.basemap;

          // Sync map visibility with Redux state to prevent mismatch
          currentLayer.visible = isActive;

          resultList.push({
            id: sublayerId,
            uniqueId,
            service,
            active: isActive,
            title,
          });
        } else {
          layersList.push(...sublayers);
        }
      }
    } catch (e) {
      // Log the error, but allow other layers to load
      // eslint-disable-next-line no-console
      console.error('Unable to list sublayers for', service.display_name);
      // eslint-disable-next-line no-console
      console.error(e);
    }
  }

  return {
    id: service.gisLayerID,
    title: service.display_name || parent.title,
    service,
    active:
      !!service.auto_checked ||
      service.service_type === GISServiceTypes.basemap,
    sublayers: resultList,
  };
}

function getSublayers(layer: TreeLayer) {
  return Array.from(layer.layers || layer.sublayers || []);
}

/**
 * Returns the list of visible sublayers for a given layer, or the layer itself
 * if it has no sublayers. This handles the differences between layer types:
 * - MapImageLayer uses allSublayers
 * - WMSLayer uses sublayers
 * - FeatureLayer groups use layers
 */
export function getQueryableLayers(layer: TreeLayer): TreeLayer[] {
  const sublayerCollection =
    layer.allSublayers || layer.sublayers || layer.layers;

  if (sublayerCollection?.length) {
    return Array.from(sublayerCollection as Iterable<TreeLayer>).filter(
      s => s.visible !== false,
    );
  }
  return [layer];
}
